<script setup lang="ts">
  import { useSectionLineUploadCollectionsStore } from '@/stores/sectionLineUploadCollections'
  import GenericDialog from '../dialog/GenericDialog.vue'
  import { ref, computed } from 'vue'

  const sectionLineUploadCollectionsStore = useSectionLineUploadCollectionsStore()
  const { addSectionLineShapeFileDialog } = storeToRefs(sectionLineUploadCollectionsStore)
  const title = computed(() => addSectionLineShapeFileDialog.value.selectedMode)

  const isLoading = ref(false)

  const projectStore = useProjectStore()
  const { currentItem: currentProject } = storeToRefs(projectStore)
  const { loadingFieldNames, fieldNames, fetchGeometryFieldNames } = useShapeFileUpload()

  const selectedMode = computed(() => addSectionLineShapeFileDialog.value.selectedMode)
  const form = ref({
    name: '',
    fieldName: null,
    files: [],
    tableName: null,
    sectionCodeField: null
  })

  const onChangeFile = computed({
    get: () => form.value.files,
    set: (newValue) => {
      form.value.files = newValue
      fetchGeometryFieldNames(newValue)
    }
  })

  const isCreateDisabled = computed(() => {
    const formRef = form.value
    if (selectedMode.value === 'Upload') {
      return formRef.files.length === 0 || !formRef.name
    } else {
      return !formRef.name
    }
  })

  const performUpload = async () => {
    if (!currentProject.value) return
    const formRef = form.value
    const formData = new FormData()
    const files = Array.isArray(formRef.files) ? formRef.files : [formRef.files]
    formData.append('file', files[0])
    formData.append('zone_field_name', formRef.fieldName || '')
    formData.append('source_table_name', formRef.tableName || '')
    formData.append('section_code_field', formRef.sectionCodeField || '')
    formData.append('section_type', formRef.sectionType)
    formData.append('project', currentProject.value.id.toString())
    formData.append('name', formRef.name)
    await sectionLineUploadCollectionsStore.uploadShapeFile(formData)
  }

  const performCreate = async () => {
    if (!currentProject.value) return
    const formRef = form.value
    const body = {
      project: currentProject.value.id,
      section_type: formRef.sectionType,
      name: formRef.name
    }
    await sectionLineUploadCollectionsStore.create(body)
    sectionLineUploadCollectionsStore.toggleAddSectionLineShapeFileDialog()
  }
  const handleSubmit = async () => {
    isLoading.value = true
    try {
      if (selectedMode.value === 'Upload') {
        await performUpload()
      } else {
        await performCreate()
      }
    } catch (error) {
      console.error(error)
    } finally {
      isLoading.value = false
    }
  }
</script>

<template>
  <GenericDialog
    v-model="addSectionLineShapeFileDialog.show"
    :max-width="600"
    @clicked-outside="addSectionLineShapeFileDialog.show = false"
  >
    <template #title>
      {{
        title === 'Upload'
          ? 'Upload a New Dataset of Section Lines'
          : 'Create a New Dataset of Section Lines'
      }}
    </template>
    <template #default>
      <v-text-field
        color="primary"
        label="Dataset name"
        v-model="form.name"
        type="text"
        :clearable="true"
      ></v-text-field>

      <template v-if="selectedMode === 'Upload'">
        <div key="Upload">
          <v-file-input
            density="comfortable"
            color="primary"
            v-model="onChangeFile"
            prepend-icon="mdi-folder-zip"
            class="mt-3"
            label="Upload .zip containing the .shp file"
            accept=".zip"
            hide-details
          ></v-file-input>
          <v-row class="mt-2">
            <v-col cols="1"></v-col>
            <v-col>
              <v-tooltip
                text="This field is used to link the uploaded lines and polygon to each other."
              >
                <template v-slot:activator="{ props }">
                  <v-select
                    v-bind="props"
                    density="comfortable"
                    :loading="loadingFieldNames"
                    :items="fieldNames"
                    filled
                    color="primary"
                    label="Select section code field"
                    v-model="form.sectionCodeField"
                  >
                  </v-select>
                </template>
              </v-tooltip>
              <v-select
                density="comfortable"
                :loading="loadingFieldNames"
                :items="fieldNames"
                filled
                persistent-hint
                color="primary"
                hint="The values in this field will be displayed as a header in the section figures"
                label="Select title field"
                v-model="form.tableName"
              ></v-select>
            </v-col>
          </v-row>
        </div>
      </template>
    </template>
    <template #actions
      ><v-btn
        color="primary"
        variant="text"
        @click="sectionLineUploadCollectionsStore.toggleAddSectionLineShapeFileDialog()"
        >Cancel
      </v-btn>
      <v-btn
        :disabled="isCreateDisabled"
        color="primary"
        @click="handleSubmit"
        :loading="isLoading"
      >
        {{ selectedMode === 'Upload' ? 'Upload' : 'Create' }}
      </v-btn></template
    >
  </GenericDialog>
</template>
